// Note: navigator.onLine is not reliable enough on its own. It reports true for a connection that
// goes nowhere - a captive portal, a dead access point, and DevTools offline emulation after a reload,
// which is how a session ended up believing it was online while every request failed with
// ERR_INTERNET_DISCONNECTED. That belief matters: the mutation wrappers only queue when the app thinks
// it is offline, so the whole offline feature quietly stops engaging.
//
// So reachability is tracked from what actually happens to requests: a network-level failure (no
// response at all) marks us unreachable, any successful response marks us reachable again.

let reachable = true
const listeners = new Set<() => void>()

function notify() {
  for (const listener of listeners) listener()
}

export function markServerReachable(): void {
  if (reachable) return
  reachable = true
  notify()
}

export function markServerUnreachable(): void {
  if (!reachable) return
  reachable = false
  notify()
}

export function isServerReachable(): boolean {
  return reachable
}

// Note: the single answer to "can we talk to the server right now". Use this rather than
// navigator.onLine when deciding whether to queue a change or send it - queueing must happen whenever
// the request would not get through, not only when the browser admits it has no network.
export function isOnlineNow(): boolean {
  return navigator.onLine && reachable
}

export function subscribeToReachability(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

// Note: the browser's own events are authoritative when they fire - regaining the network clears a
// previously detected failure so the app does not stay stuck as offline.
window.addEventListener('online', markServerReachable)
window.addEventListener('offline', markServerUnreachable)
